import React, { useMemo } from "react";
import { Button, Col, Form, Row, Select, Space, Switch, Table } from "antd";
import type { FormInstance, TableProps } from "antd";
import { CompanyAdminInProps, Role } from "src/entity/map-company-user/user-in-company";
import { Company } from "src/entity/map-company-user/map-company-user";
import { UserStateProps } from "src/entity/user-management/users-state.entity";
import { BsTrashFill } from "react-icons/bs";
import { PlusCircleOutlined } from "@ant-design/icons";
import WrapCenter from "src/components/WrapCenter/WrapCenter";
import "./ManageUserTable.css";
import { parseErrorString } from "src/utils/utils-object";
import { RolesEnum } from "src/entity/common/roles.enum";

type Roles = Role[] | null;
type Companies = Company[] | null;
interface ManageUserTableProps {
  roleCompanyForm: FormInstance;
  roles: Roles | undefined;
  company: Companies;
  onAddUser: () => void;
  manageUsers: UserStateProps[];
  onUpdateKeyVal: (row: string, field: keyof UserStateProps, value: string | boolean) => void;
  onDeleteCompanyRole: (index: string | number) => void;
  errorMessage: string | null;
  clearError: () => void;
  isSystemAdmin: boolean;
  activeCompanyId: string;
  isEditUser: string;
  activeUser: string | undefined;
  companyAdminIn: CompanyAdminInProps[];
}
const wrapper_form = {
  marginBottom: 0,
};
const getWrapperSlotStyle = () => {
  return {
    ...wrapper_form,
  };
};

const getColumns = (
  roles: Roles | undefined,
  company: Companies,
  validateCompanyRole: any,
  onUpdateKeyVal: any,
  onDeleteCompanyRole: any,
  roleCompanyForm: FormInstance,
  hasSystemAdmin: boolean,
  rolesDropdown: Roles | undefined,
): any => {
  const columns: TableProps<UserStateProps>["columns"] = [
    {
      title: "No",
      key: "no",
      width: "5rem",
      render: (_, __, index) => index + 1,
      align: "left",
    },
    {
      title: (
        <span>
          Role <span style={{ color: "red" }}>*</span>
        </span>
      ),
      key: "role",
      align: "left",
      render: (_, record) => {
        return (
          <Col style={getWrapperSlotStyle()}>
            <Form.Item
              style={getWrapperSlotStyle()}
              name={"role-" + record.key}
              rules={[{ validator: (rule, val) => validateCompanyRole(rule, val, record, "role") }]}
            >
              <Select className="border-transparent" onChange={(value) => onUpdateKeyVal(record.key, "role", value)}>
                {rolesDropdown &&
                  rolesDropdown.length > 0 &&
                  rolesDropdown.map((item) => {
                    return (
                      <Select.Option key={item._id} value={item._id} disabled={item.roleValue === RolesEnum.SYSTEM_ADMIN && hasSystemAdmin}>
                        {item.roleName}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
        );
      },
    },
    {
      title: (
        <span>
          Company <span style={{ color: "red" }}>*</span>
        </span>
      ),
      key: "company",
      align: "left",
      render: (_, record) => {
        const getRole = roleCompanyForm.getFieldValue("role-" + record.key);
        const currentRole = Array.isArray(getRole) ? getRole[0] : getRole;
        const findSystemAdmin = roles?.find((item) => item.roleValue === RolesEnum.SYSTEM_ADMIN);
        // console.log("Get Role & FInd SystemAdmin : ", currentRole, findSystemAdmin);
        return (
          <Col style={getWrapperSlotStyle()}>
            <Form.Item
              style={getWrapperSlotStyle()}
              name={"company-" + record.key}
              rules={[{ validator: (rule, val) => validateCompanyRole(rule, val, record, "company") }]}
              {...(currentRole === findSystemAdmin?._id ? { hidden: true } : {})}
            >
              <Select className="border-transparent" onChange={(value) => onUpdateKeyVal(record.key, "company", value)}>
                {company &&
                  company.length > 0 &&
                  company.map((item) => {
                    return (
                      <Select.Option key={item._id} value={item._id}>
                        {item.companyName}
                      </Select.Option>
                    );
                  })}
              </Select>
            </Form.Item>
          </Col>
        );
      },
    },
    {
      title: "Status",
      key: "isActive",
      align: "left",
      render: (_, record) => {
        return (
          <Col style={getWrapperSlotStyle()}>
            <Form.Item
              name={"isActive-" + record.key}
              rules={[]}
              style={getWrapperSlotStyle()}
              //   className="h-full flex flex-col justify-center"
            >
              <Space size="middle">
                <Switch
                  value={record.isActive}
                  onChange={(value) => onUpdateKeyVal(record.key, "isActive", value)}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  defaultChecked
                />
              </Space>
            </Form.Item>
          </Col>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "left",
      render: (_, record, index) => (
        <Space size={"middle"}>
          <Button className="border-transparent bg-transparent" size={"small"} onClick={() => onDeleteCompanyRole(index)}>
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
    },
  ];
  return columns;
};

const ManageUserTable: React.FC<ManageUserTableProps> = ({
  roles,
  company,
  onAddUser,
  manageUsers,
  roleCompanyForm,
  onUpdateKeyVal,
  onDeleteCompanyRole,
  errorMessage,
  clearError,
  isSystemAdmin,
  activeCompanyId,
  isEditUser,
  activeUser,
  companyAdminIn,
}) => {
  //   console.log("Manage Users : ", manageUsers);
  //   console.log("Form Values : ", roleCompanyForm.getFieldsValue());

  const validateCompanyRole = (_: any, value: any, record: UserStateProps, changeKey: string) => {
    if (changeKey === "company") {
      const uniqKey = `${value}-${record.role}`;
      if (manageUsers.filter((item) => `${item.company}-${item.role}` === uniqKey).length > 1) {
        return Promise.reject("Duplicate company and role.");
      } else {
        return Promise.resolve();
      }
    } else if (changeKey === "role") {
      const uniqKey = `${record.company}-${value}`;
      if (manageUsers.filter((item) => `${item.company}-${item.role}` === uniqKey).length > 1) {
        return Promise.reject("Duplicate company and role.");
      } else {
        return Promise.resolve();
      }
    }
    return Promise.resolve();
  };

  // console.log("DETAILS :: ", { company, activeCompanyId });
  const companyDropdown = useMemo(() => {
    const companyAdminInLists = companyAdminIn.map((e) => e.company);
    return isSystemAdmin && company
      ? company
      : isEditUser === activeUser
        ? company
        : company?.filter((item) => companyAdminInLists.includes(item._id));
  }, [isSystemAdmin, activeCompanyId, companyAdminIn, isEditUser, activeUser]);

  const rolesDropdown = useMemo(() => {
    return isSystemAdmin && roles ? roles : roles?.filter((item) => item.roleValue !== RolesEnum.SYSTEM_ADMIN);
  }, [isSystemAdmin, activeCompanyId, roles, manageUsers]);

  const hasSystemAdmin = useMemo(() => {
    const findSystemAdmin = roles?.find((item) => item.roleValue === RolesEnum.SYSTEM_ADMIN);
    // console.log("Find system admin : ", findSystemAdmin);
    return manageUsers.some((item) => item.role === findSystemAdmin?._id);
  }, [roles, manageUsers]);

  // console.log("companyDropdown :: ", companyDropdown);
  // console.log("Properties : ", { hasSystemAdmin, manageUsers });

  return (
    <Form
      form={roleCompanyForm}
      name={"role-company-form"}
      onFinish={() => console.log()}
      onFieldsChange={(_) => {
        if (errorMessage && errorMessage.includes("Duplicate Role")) {
          clearError();
        }
      }}
    >
      <Table
        id={"table-user-management"}
        columns={getColumns(
          roles,
          companyDropdown as Companies,
          validateCompanyRole,
          onUpdateKeyVal,
          onDeleteCompanyRole,
          roleCompanyForm,
          hasSystemAdmin,
          rolesDropdown,
        )}
        rowKey={(item) => `${item.key}-${item.role}-${item.company}`}
        dataSource={manageUsers}
        pagination={false}
        scroll={{ y: "40vh" }}
      />
      <Row className="w-full mt-4" justify={"end"}>
        <Button type={"primary"} onClick={onAddUser}>
          <WrapCenter>
            <PlusCircleOutlined />
          </WrapCenter>
        </Button>
      </Row>
    </Form>
  );
};

export default ManageUserTable;
